import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { getApiErrorMessage } from '../lib/errorUtils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { IntakeAliasesSection } from './CompanySettingsPage';  // v3.18-1385 GMAIL-005
import CompanyCommunicationsPanel from '../components/CompanyCommunicationsPanel';  // v3.18-1385 GMAIL-005
// v3.18-1245 PARTNER-FIN-001 — Partner financial sync panel.
import PartnerFinancialSyncPanel from '../components/PartnerFinancialSyncPanel';
import {
  ArrowLeft,
  Building2,
  Plus,
  Loader2,
  DollarSign,
  Receipt,
  Send,
  CheckCircle,
  Clock,
  TrendingUp,
  TrendingDown,
  RefreshCw,
  Trash2,
  Users,
  Edit,
  Mail,
  MailCheck,
  Phone,
  Shield,
  Eye,
  Printer,
  Ban,
  Power,
  Wallet,
  Landmark,
  FolderOpen,
  Settings as SettingsIcon,
  Briefcase,
  KeyRound,
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

// v3.15-1187 ADMIN-001 — Role catalog. Purely presentational: powers the
// Roles tab display and (in future tasks) the multi-role assignment picker.
// NOT wired to compensation, permissions, or payroll — this is scaffolding.
const COMPANY_ROLE_CATALOG = {
  // Internal PTC brand
  ptc: {
    label: 'Prestigious Travel Club',
    roles: ['Director', 'Manager', 'Sales Rep', 'Presenter', 'VO Officer'],
  },
  // Internal EasyQuickFund
  eqf: {
    label: 'EasyQuickFund',
    roles: ['Director', 'Manager', 'Application Concierge'],
  },
  // External marketing partners
  marketing: {
    label: 'Marketing Partners',
    roles: ['Marketing Partner'],
  },
  // Business funding line — reserved for future TASK
  business_funding: {
    label: 'Business Funding',
    roles: ['Reserved'],
  },
  // CF360 — reserved
  cf360: {
    label: 'CreditForward360',
    roles: ['Reserved'],
  },
};

// Resolve which catalog bucket applies to a given company row. Fails open
// to marketing partners if no signal — safest default for external companies.
const resolveCompanyCatalogKey = (company) => {
  if (!company) return 'marketing';
  const name = (company.name || '').toLowerCase();
  const type = (company.company_type || '').toLowerCase();
  const industry = (company.industry_category || '').toLowerCase();
  if (type === 'internal' && name.includes('prestigious travel')) return 'ptc';
  if (type === 'internal' && name.includes('easyquickfund')) return 'eqf';
  if (industry === 'business_funding' || name.includes('business funding')) return 'business_funding';
  if (industry === 'credit_repair' || name.includes('creditforward') || name.includes('cf360')) return 'cf360';
  return 'marketing';
};

const CompanyDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isInternal, isAdmin } = useAuth();
  // v3.21-1613 · External partner users no longer see the internal-only
  // Roles / Compensation / Communications tabs — they belong to PTC's
  // internal role architecture, compensation architecture, and internal
  // communications administration, and are gated on the backend with
  // HTTP 403 as well.  `isInternal()` here mirrors backend
  // `services/scope_guard.is_internal_user` (via lib/isInternalUser.js).
  const isInternalUser = typeof isInternal === 'function' ? isInternal() : !!isInternal;
  
  const [company, setCompany] = useState(null);
  const [financialSummary, setFinancialSummary] = useState(null);
  const [payments, setPayments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [isPaymentOpen, setIsPaymentOpen] = useState(false);
  const [isInvoiceOpen, setIsInvoiceOpen] = useState(false);
  const [isUserDialogOpen, setIsUserDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // User form
  const [userForm, setUserForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    role: 'company_team_member',
    password: '',
    // v3.15-1188 ADMIN-002 — Company assignment + role-change audit note.
    company_id: '',
    role_change_note: '',
    // v3.15-1189 ADMIN-003 — Multi-role assignments. Each entry:
    //   { role, level, effective_date, is_active, note, compensation_plan_id? }
    // v3.15-1193 COMP-002 — `compensation_plan_id` optionally links each
    // multi-role assignment to a Company-scoped compensation plan.
    additional_roles: [],
    // v3.15-1193 COMP-002 — Top-level compensation plans assigned to the
    // employee (multi-select). Plans are Company-scoped; the picker filters
    // to plans belonging to userForm.company_id.
    compensation_plan_ids: [],
  });

  // v3.15-1188 ADMIN-002 — Companies list (for the "Company Assignment"
  // dropdown in the Edit Employee dialog). Fetched lazily on tab entry.
  const [allCompanies, setAllCompanies] = useState([]);
  const [historyUser, setHistoryUser] = useState(null);
  
  // Form states
  const [newPayment, setNewPayment] = useState({
    amount: '',
    wire_date: format(new Date(), 'yyyy-MM-dd'),
    wire_reference: '',
    bank_name: '',
    client_id: '',
    notes: '',
  });
  
  const [newInvoice, setNewInvoice] = useState({
    invoice_number: '',
    amount: '',
    invoice_date: format(new Date(), 'yyyy-MM-dd'),
    due_date: '',
    client_id: '',
    notes: '',
  });

  // v3.15-951: view modals for Invoices + Wire Payments
  const [viewInvoice, setViewInvoice] = useState(null);
  const [viewPayment, setViewPayment] = useState(null);
  const [editInvoiceOpen, setEditInvoiceOpen] = useState(false);
  const [editPaymentOpen, setEditPaymentOpen] = useState(false);
  const [editInvoiceForm, setEditInvoiceForm] = useState(null);
  const [editPaymentForm, setEditPaymentForm] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);

  // v3.15-1187 ADMIN-001 — Team tab helpers. NO business logic — these are
  // thin passthroughs to endpoints that already exist for the HR page.
  const [sendingCredentialsIds, setSendingCredentialsIds] = useState(() => new Set());
  const [viewingUser, setViewingUser] = useState(null);

  // v3.15-1192 COMP-001 — Company Compensation Foundation.
  // Administrative store only — plans belong to the company, employees may
  // reference plans via `compensation_plan_ids`. NO calculation logic in
  // this task; a future COMP task owns the calc surface.
  const [compensationPlans, setCompensationPlans] = useState([]);
  const [isPlanDialogOpen, setIsPlanDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [savingPlan, setSavingPlan] = useState(false);
  const [planForm, setPlanForm] = useState({
    role: '',
    level: 1,
    effective_date: '',
    expiration_date: '',
    status: 'active',
    notes: '',
  });

  // v3.15-1194 COMP-003 — Compensation Rule Engine (storage only).
  // Rules dialog is a separate surface — reached via the "Rules" action on
  // each Compensation Plan row. STORAGE ONLY. No calculations execute here.
  const [isRulesDialogOpen, setIsRulesDialogOpen] = useState(false);
  const [rulesPlan, setRulesPlan]     = useState(null);
  const [ruleList, setRuleList]       = useState([]);
  const [editingRule, setEditingRule] = useState(null);
  const [savingRule, setSavingRule]   = useState(false);
  const [ruleForm, setRuleForm]       = useState({
    event: '',
    calculation_type: 'fixed',
    value: '',
    effective_date: '',
    expiration_date: '',
    status: 'active',
    notes: '',
  });

  // Event catalog is intentionally client-side (kept in sync with backend
  // SUPPORTED_EVENTS). No compensation logic is derived from labels.
  const COMPENSATION_EVENTS = [
    { group: 'EasyQuickFund',        options: [
      { value: 'eqf.application_submitted',    label: 'Application Submitted' },
      { value: 'eqf.vendor_payment_received',  label: 'Vendor Payment Received' },
    ]},
    { group: 'Prestigious Travel Club', options: [
      { value: 'ptc.vendor_payment_received',  label: 'Vendor Payment Received' },
      { value: 'ptc.paid_in_full',             label: 'Paid In Full' },
    ]},
    { group: 'Solar Exit',            options: [
      { value: 'solar.paid_in_full',           label: 'Paid In Full' },
    ]},
    { group: 'Manual',                options: [
      { value: 'manual.bonus',                 label: 'Bonus' },
      { value: 'manual.spiff',                 label: 'Spiff' },
      { value: 'manual.adjustment',            label: 'Adjustment' },
    ]},
  ];

  const openRulesForPlan = async (plan) => {
    setRulesPlan(plan);
    setIsRulesDialogOpen(true);
    try {
      const res = await api.get(`/companies/${id}/compensation-plans/${plan.id}/rules`);
      setRuleList(Array.isArray(res.data?.items) ? res.data.items : []);
    } catch (err) {
      setRuleList([]);
    }
  };

  const openAddRule = () => {
    setEditingRule(null);
    setRuleForm({
      event: '',
      calculation_type: 'fixed',
      value: '',
      effective_date: format(new Date(), 'yyyy-MM-dd'),
      expiration_date: '',
      status: 'active',
      notes: '',
    });
  };

  const openEditRule = (rule) => {
    setEditingRule(rule);
    setRuleForm({
      event: rule.event || '',
      calculation_type: rule.calculation_type || 'fixed',
      value: rule.value == null ? '' : String(rule.value),
      effective_date: rule.effective_date || '',
      expiration_date: rule.expiration_date || '',
      status: rule.status || 'active',
      notes: rule.notes || '',
    });
  };

  const handleSaveRule = async (e) => {
    e.preventDefault();
    if (!ruleForm.event) { toast.error('Compensation Event is required'); return; }
    if (!ruleForm.calculation_type) { toast.error('Calculation Type is required'); return; }
    setSavingRule(true);
    try {
      const payload = {
        event:            ruleForm.event,
        calculation_type: ruleForm.calculation_type,
        value:            (ruleForm.value || '').toString(),
        effective_date:   ruleForm.effective_date || null,
        expiration_date:  ruleForm.expiration_date || null,
        status:           ruleForm.status,
        notes:            ruleForm.notes || null,
      };
      if (editingRule) {
        await api.put(`/companies/${id}/compensation-plans/${rulesPlan.id}/rules/${editingRule.id}`, payload);
        toast.success('Rule updated');
      } else {
        await api.post(`/companies/${id}/compensation-plans/${rulesPlan.id}/rules`, payload);
        toast.success('Rule created');
      }
      setEditingRule(null);
      setRuleForm({ event: '', calculation_type: 'fixed', value: '', effective_date: '', expiration_date: '', status: 'active', notes: '' });
      const res = await api.get(`/companies/${id}/compensation-plans/${rulesPlan.id}/rules`);
      setRuleList(Array.isArray(res.data?.items) ? res.data.items : []);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to save rule'));
    } finally {
      setSavingRule(false);
    }
  };

  const handleDeleteRule = async (ruleId) => {
    try {
      await api.delete(`/companies/${id}/compensation-plans/${rulesPlan.id}/rules/${ruleId}`);
      toast.success('Rule deleted');
      const res = await api.get(`/companies/${id}/compensation-plans/${rulesPlan.id}/rules`);
      setRuleList(Array.isArray(res.data?.items) ? res.data.items : []);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to delete rule'));
    }
  };

  const fetchCompensationPlans = useCallback(async () => {
    try {
      const res = await api.get(`/companies/${id}/compensation-plans`);
      setCompensationPlans(Array.isArray(res.data?.items) ? res.data.items : []);
    } catch (err) {
      // Silently ignore — this is administrative UX; toast on write path only.
      console.error('Failed to load compensation plans', err);
    }
  }, [id]);

  const openAddPlan = () => {
    setEditingPlan(null);
    setPlanForm({
      role: '',
      level: 1,
      effective_date: format(new Date(), 'yyyy-MM-dd'),
      expiration_date: '',
      status: 'active',
      notes: '',
    });
    setIsPlanDialogOpen(true);
  };

  const openEditPlan = (plan) => {
    setEditingPlan(plan);
    setPlanForm({
      role: plan.role || '',
      level: plan.level ?? 1,
      effective_date: plan.effective_date || '',
      expiration_date: plan.expiration_date || '',
      status: plan.status || 'active',
      notes: plan.notes || '',
    });
    setIsPlanDialogOpen(true);
  };

  const handleSavePlan = async (e) => {
    e.preventDefault();
    if (!planForm.role || !planForm.role.trim()) {
      toast.error('Role is required');
      return;
    }
    setSavingPlan(true);
    try {
      const payload = {
        role:            planForm.role.trim(),
        level:           Number(planForm.level) || 1,
        effective_date:  planForm.effective_date || null,
        expiration_date: planForm.expiration_date || null,
        status:          planForm.status,
        notes:           planForm.notes || null,
      };
      if (editingPlan) {
        await api.put(`/companies/${id}/compensation-plans/${editingPlan.id}`, payload);
        toast.success('Compensation plan updated');
      } else {
        await api.post(`/companies/${id}/compensation-plans`, payload);
        toast.success('Compensation plan created');
      }
      setIsPlanDialogOpen(false);
      fetchCompensationPlans();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to save compensation plan'));
    } finally {
      setSavingPlan(false);
    }
  };

  const handleDeletePlan = async (planId) => {
    try {
      await api.delete(`/companies/${id}/compensation-plans/${planId}`);
      toast.success('Compensation plan deleted');
      fetchCompensationPlans();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to delete compensation plan'));
    }
  };

  // v3.15-1193 COMP-002 — Employee ↔ Plan assignment picker source.
  // Plans MUST be scoped to the employee's assigned company (userForm.company_id),
  // which may differ from the currently-viewed company when reassigning. Loaded
  // lazily when the Edit dialog opens or the assignment changes.
  const [employeePlans, setEmployeePlans] = useState([]);

  useEffect(() => {
    if (!isUserDialogOpen) return;
    const targetCompany = userForm.company_id || id;
    if (!targetCompany) { setEmployeePlans([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/companies/${targetCompany}/compensation-plans?status=active`);
        if (!cancelled) setEmployeePlans(Array.isArray(res.data?.items) ? res.data.items : []);
      } catch (err) {
        if (!cancelled) setEmployeePlans([]);
      }
    })();
    return () => { cancelled = true; };
  }, [isUserDialogOpen, userForm.company_id, id]);

  const handleSendCredentials = async (userId) => {
    if (!userId) return;
    setSendingCredentialsIds(prev => {
      const next = new Set(prev);
      next.add(userId);
      return next;
    });
    try {
      const res = await api.post('/users/send-credentials', { userIds: [userId], confirm: true });
      const sent   = res?.data?.summary?.sent   ?? 0;
      const failed = res?.data?.summary?.failed ?? 0;
      if (sent > 0) toast.success('Credentials email sent');
      if (failed > 0) {
        const reason = res?.data?.failed?.[0]?.error || 'unknown error';
        toast.error(`Failed to send credentials (${reason})`);
      }
      fetchData();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to send credentials'));
    } finally {
      setSendingCredentialsIds(prev => {
        const next = new Set(prev);
        next.delete(userId);
        return next;
      });
    }
  };

  const handleToggleLogin = async (user) => {
    try {
      // v3.21-1616 · The new backend endpoints normalize approval_status
      // + user_status + is_active in one call and honor company-scoped
      // partner authority (Director / Manager / Company Admin /
      // Manager Director) in addition to PTC admin.  A legacy user
      // stuck at is_active=true but user_status=inactive still needs
      // Activate — so we route on the composite "resolved active"
      // signal, not just is_active.
      const looksActive = user.is_active !== false
        && (user.user_status || 'active') === 'active'
        && (user.approval_status || 'approved') === 'approved';
      if (!looksActive) {
        await api.post(`/users/${user.id}/activate`);
        toast.success('Account activated');
      } else {
        await api.post(`/users/${user.id}/deactivate`);
        toast.success('Account deactivated');
      }
      fetchData();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to update account status'));
    }
  };

  const fetchData = useCallback(async () => {
    // v3.21-1647 · P0 · Primary Company Lookup Correction.
    //
    // Previous impl called `GET /companies` (default limit=100), then
    // `.find(c => c.id === id)` client-side.  If the target company
    // was outside the returned page slice for any reason (paginated
    // limit, filter, ordering, access scoping) `.find()` returned
    // `undefined` and the UI falsely rendered "Company not found" —
    // observed on Freedom Solutions Group LLC despite every scoped
    // endpoint (`/companies/{id}`, `/financial-summary`, `/payments`,
    // `/invoices`) returning 200.
    //
    // The primary lookup now hits `/companies/{id}` directly.  A
    // genuine 404 still surfaces "Company not found"; any secondary
    // financial subsection failure no longer poisons the shell.
    try {
      const companyRes = await api.get(`/companies/${id}`);
      setCompany(companyRes.data);
    } catch (error) {
      const status = error?.response?.status;
      console.error('Company lookup failed:', status, error);
      if (status === 404) {
        setCompany(null);
      } else if (status === 403) {
        toast.error('You do not have access to this company');
        setCompany(null);
      } else {
        toast.error('Failed to load company');
        setCompany(null);
      }
      setLoading(false);
      return;
    }

    // Secondary financial subsections — each fetch is isolated so a
    // single failure never invalidates the company shell.  Errors are
    // logged; the affected section stays in its empty/default state.
    const [summaryRes, paymentsRes, invoicesRes, clientsRes, usersRes, allCompaniesRes] = await Promise.all([
      api.get(`/companies/${id}/financial-summary`).catch((e) => {
        console.error('financial-summary failed:', e);
        toast.error('Financial summary unavailable');
        return { data: null };
      }),
      api.get(`/companies/${id}/payments`).catch((e) => {
        console.error('payments failed:', e);
        return { data: [] };
      }),
      api.get(`/companies/${id}/invoices`).catch((e) => {
        console.error('invoices failed:', e);
        return { data: [] };
      }),
      // v3.18-1246 PARTNER-FIN-001 P0-A — Company Detail is a Partner
      // Financial Management workspace, not an operational CRM active
      // view. Finance needs Paid In Full / Funded clients visible so
      // they remain selectable in the Invoice + Wire dialogs and roll
      // up into the "We Owe Partner" financials. status_filter=all
      // reuses the existing endpoint contract — no new logic, no
      // duplicate query. Operational active views (ClientsPage,
      // PtcDealPipelinePage, DashboardPage, CalendarPage) continue to
      // omit status_filter and thus default to "active" as before.
      api.get(`/clients?company_id=${id}&status_filter=all`).catch((e) => {
        console.error('clients failed:', e);
        return { data: [] };
      }),
      api.get(`/users/by-company/${id}`).catch(() => ({ data: [] })),
      // v3.15-1188 ADMIN-002 — companies catalog for reassignment dropdown.
      api.get(`/companies`).catch(() => ({ data: [] })),
    ]);

    setFinancialSummary(summaryRes.data);
    setPayments(Array.isArray(paymentsRes.data) ? paymentsRes.data : []);
    setInvoices(Array.isArray(invoicesRes.data) ? invoicesRes.data : []);
    const rawClients = clientsRes.data?.clients || clientsRes.data || [];
    setClients(Array.isArray(rawClients) ? rawClients : []);
    setCompanyUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
    setAllCompanies(Array.isArray(allCompaniesRes.data) ? allCompaniesRes.data : []);
    setLoading(false);
  }, [id]);

  useEffect(() => {
    fetchData();
    fetchCompensationPlans();
  }, [fetchData, fetchCompensationPlans]);

  const handleCreatePayment = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      await api.post(`/companies/${id}/payments`, {
        company_id: id,
        amount: parseFloat(newPayment.amount),
        wire_date: newPayment.wire_date,
        wire_reference: newPayment.wire_reference || null,
        bank_name: newPayment.bank_name || null,
        client_id: newPayment.client_id || null,
        notes: newPayment.notes || null,
      });
      
      toast.success('Payment recorded successfully');
      setIsPaymentOpen(false);
      setNewPayment({
        amount: '',
        wire_date: format(new Date(), 'yyyy-MM-dd'),
        wire_reference: '',
        bank_name: '',
        client_id: '',
        notes: '',
      });
      fetchData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to record payment'));
    } finally {
      setSaving(false);
    }
  };

  const handleCreateInvoice = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      await api.post(`/companies/${id}/invoices`, {
        company_id: id,
        invoice_number: newInvoice.invoice_number,
        amount: parseFloat(newInvoice.amount),
        invoice_date: newInvoice.invoice_date,
        due_date: newInvoice.due_date || null,
        client_id: newInvoice.client_id || null,
        notes: newInvoice.notes || null,
      });
      
      toast.success('Invoice created successfully');
      setIsInvoiceOpen(false);
      setNewInvoice({
        invoice_number: '',
        amount: '',
        invoice_date: format(new Date(), 'yyyy-MM-dd'),
        due_date: '',
        client_id: '',
        notes: '',
      });
      fetchData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to create invoice'));
    } finally {
      setSaving(false);
    }
  };

  const handleMarkInvoicePaid = async (invoiceId) => {
    try {
      await api.post(`/companies/${id}/invoices/${invoiceId}/mark-paid`);
      toast.success('Invoice marked as paid');
      fetchData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to mark invoice as paid'));
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Are you sure you want to delete this payment record?')) return;
    try {
      await api.delete(`/companies/${id}/payments/${paymentId}`);
      toast.success('Payment deleted');
      setViewPayment(null);
      fetchData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to delete payment'));
    }
  };

  const handleDeleteInvoice = async (invoiceId) => {
    if (!window.confirm('Are you sure you want to delete this invoice?')) return;
    try {
      await api.delete(`/companies/${id}/invoices/${invoiceId}`);
      toast.success('Invoice deleted');
      setViewInvoice(null);
      fetchData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to delete invoice'));
    }
  };

  // v3.15-951: edit/save invoice + wire payment from the detail modals.
  // Totals and creation flows are untouched.
  const openEditInvoice = () => {
    if (!viewInvoice) return;
    setEditInvoiceForm({
      invoice_number: viewInvoice.invoice_number || '',
      amount:         viewInvoice.amount ?? '',
      invoice_date:   viewInvoice.invoice_date || '',
      due_date:       viewInvoice.due_date || '',
      paid_date:      viewInvoice.paid_date || '',
      notes:          viewInvoice.notes || '',
      status:         viewInvoice.status || 'pending',
    });
    setEditInvoiceOpen(true);
  };
  const saveInvoiceEdit = async (e) => {
    e.preventDefault();
    if (!viewInvoice) return;
    setSavingEdit(true);
    try {
      const payload = { ...editInvoiceForm };
      if (payload.amount !== '' && payload.amount !== null) payload.amount = parseFloat(payload.amount);
      const { data } = await api.put(`/companies/${id}/invoices/${viewInvoice.id}`, payload);
      toast.success('Invoice updated');
      setEditInvoiceOpen(false);
      setViewInvoice(data || { ...viewInvoice, ...payload });
      fetchData();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to update invoice'));
    } finally {
      setSavingEdit(false);
    }
  };
  const openEditPayment = () => {
    if (!viewPayment) return;
    setEditPaymentForm({
      wire_date:      viewPayment.wire_date || '',
      amount:         viewPayment.amount ?? '',
      wire_reference: viewPayment.wire_reference || '',
      bank_name:      viewPayment.bank_name || '',
      notes:          viewPayment.notes || '',
    });
    setEditPaymentOpen(true);
  };
  const savePaymentEdit = async (e) => {
    e.preventDefault();
    if (!viewPayment) return;
    setSavingEdit(true);
    try {
      const payload = { ...editPaymentForm };
      if (payload.amount !== '' && payload.amount !== null) payload.amount = parseFloat(payload.amount);
      const { data } = await api.put(`/companies/${id}/payments/${viewPayment.id}`, payload);
      toast.success('Wire payment updated');
      setEditPaymentOpen(false);
      setViewPayment(data || { ...viewPayment, ...payload });
      fetchData();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to update wire payment'));
    } finally {
      setSavingEdit(false);
    }
  };
  const printInvoice = () => {
    if (!viewInvoice) return;
    const w = window.open('', '_blank', 'width=800,height=900');
    if (!w) { toast.error('Popup blocked — allow popups to print'); return; }
    const rows = [
      ['Invoice #',  viewInvoice.invoice_number],
      ['Client',     viewInvoice.client_name || '—'],
      ['Amount',     formatCurrency(viewInvoice.amount)],
      ['Status',     (viewInvoice.status || 'pending').toUpperCase()],
      ['Invoice Date', viewInvoice.invoice_date || '—'],
      ['Due Date',     viewInvoice.due_date || '—'],
      ['Paid Date',    viewInvoice.paid_date || '—'],
      ['Created By',   viewInvoice.created_by_name || '—'],
    ];
    const esc = (v) => String(v ?? '').replace(/[&<>]/g, s => ({ '&':'&amp;','<':'&lt;','>':'&gt;' }[s]));
    w.document.write(`<!DOCTYPE html><html><head><title>Invoice ${esc(viewInvoice.invoice_number)}</title>
      <style>body{font-family:Arial,sans-serif;padding:32px;color:#222}h1{font-size:22px;margin-bottom:24px;border-bottom:2px solid #222;padding-bottom:8px}
      table{border-collapse:collapse;width:100%;margin-bottom:24px}td{padding:8px 12px;border-bottom:1px solid #ddd;font-size:14px}
      td:first-child{color:#666;width:160px;font-weight:500}.notes{background:#f8f9fa;padding:16px;border-radius:6px;white-space:pre-wrap;margin-top:16px}
      @media print{.noprint{display:none}}</style></head><body>
      <h1>Invoice ${esc(viewInvoice.invoice_number)}</h1>
      <table>${rows.map(r => `<tr><td>${esc(r[0])}</td><td>${esc(r[1])}</td></tr>`).join('')}</table>
      ${viewInvoice.notes ? `<div><strong>Notes</strong><div class="notes">${esc(viewInvoice.notes)}</div></div>` : ''}
      <script>window.onload=()=>window.print();<\/script></body></html>`);
    w.document.close();
  };

  // ===== USER MANAGEMENT HANDLERS =====
  // v3.15-972: Inject "VO Officer" role into the dropdown when the company is
  // industry_category == "timeshare_exit". Backend already enforces the rule
  // via routes/users.py — this just unhides the option in the UI so directors
  // can actually pick it. Falls back to the 3 default external roles.
  //
  // v3.15-1077: Inject "Manager Director" (an INTERNAL role) when the company
  // is "Prestigious Travel Club Inc". PTC Inc is the sole approved external
  // subsidiary that hosts internal-role users — same precedent as the
  // v3.15-1074 SMS access exception (case-insensitive, punctuation-collapsed
  // name match). NO other external company is affected.
  const EXTERNAL_ROLES = (() => {
    // v3.21-1631 · Canonical external partner role hierarchy.
    // Principal (owner) — protected: only PTC internal admins/directors
    // may pick this option (enforced by the disabled-check below).
    const base = [
      { value: 'company_principal',   label: 'Principal',
        principalGated: true },
      { value: 'company_director',    label: 'Director 1' },
      { value: 'manager_director',    label: 'Manager Director' },
      { value: 'company_team_member', label: 'Team Member' },
    ];
    const isTsExit = (company?.industry_category === 'timeshare_exit')
      || (company?.company_type === 'timeshare_exit');
    if (isTsExit) {
      base.push({ value: 'vo_officer', label: 'VO Officer' });
    }
    // v3.15-1077 — PTC Inc exception (internal-role hosting).
    // Manager Director is already in the canonical list above, so PTC
    // Inc simply keeps the default order.
    return base;
  })();

  const openAddUser = () => {
    setEditingUser(null);
    setUserForm({ full_name: '', email: '', phone: '', role: 'company_team_member', password: '', company_id: id, role_change_note: '', additional_roles: [], compensation_plan_ids: [] });
    setIsUserDialogOpen(true);
  };

  const openEditUser = (user) => {
    setEditingUser(user);
    setUserForm({
      full_name: user.full_name || '',
      email: user.email || '',
      phone: user.phone || '',
      role: user.role || 'company_team_member',
      password: '',
      company_id: user.company_id || id,
      role_change_note: '',
      additional_roles: Array.isArray(user.additional_roles) ? user.additional_roles.map(r => ({
        role:                  r?.role || 'company_team_member',
        level:                 r?.level ?? 1,
        effective_date:        r?.effective_date || format(new Date(), 'yyyy-MM-dd'),
        is_active:             r?.is_active !== false,
        note:                  r?.note || '',
        // v3.15-1193 COMP-002 — optional plan link per role assignment.
        compensation_plan_id:  r?.compensation_plan_id || '',
      })) : [],
      compensation_plan_ids: Array.isArray(user.compensation_plan_ids) ? user.compensation_plan_ids : [],
    });
    setIsUserDialogOpen(true);
  };

  const handleSaveUser = async (e) => {
    e.preventDefault();
    if (!userForm.full_name || !userForm.email) {
      toast.error('Name and email are required');
      return;
    }
    setSaving(true);
    try {
      if (editingUser) {
        // v3.15-1188 ADMIN-002 — include company_id + role_change_note.
        // The transient `role_change_note` is stripped on the server before
        // $set; it only affects the appended role_history entry.
        // v3.15-1189 ADMIN-003 — include additional_roles (multi-role).
        // v3.15-1193 COMP-002 — include compensation_plan_ids (top-level)
        // and per-role compensation_plan_id nested inside additional_roles.
        const payload = {
          full_name: userForm.full_name,
          email: userForm.email,
          phone: userForm.phone,
          role: userForm.role,
          company_id: userForm.company_id || editingUser.company_id,
          additional_roles: (userForm.additional_roles || []).map(r => ({
            role:                  r.role,
            level:                 Number(r.level) || 1,
            effective_date:        r.effective_date || null,
            is_active:             r.is_active !== false,
            note:                  r.note || null,
            compensation_plan_id:  r.compensation_plan_id || null,
          })),
          compensation_plan_ids: Array.isArray(userForm.compensation_plan_ids)
            ? userForm.compensation_plan_ids.filter(Boolean)
            : [],
        };
        if (userForm.password) payload.password = userForm.password;
        if (userForm.role_change_note && userForm.role !== editingUser.role) {
          payload.role_change_note = userForm.role_change_note;
        }
        await api.put(`/users/${editingUser.id}`, payload);
        toast.success('Employee updated');
      } else {
        if (!userForm.password) {
          toast.error('Password is required for new users');
          setSaving(false);
          return;
        }
        await api.post('/users', {
          full_name: userForm.full_name,
          email: userForm.email,
          phone: userForm.phone,
          role: userForm.role,
          password: userForm.password,
          company_id: userForm.company_id || id,
        });
        toast.success('Employee created');
      }
      setIsUserDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, editingUser ? 'Failed to update employee' : 'Failed to create employee'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await api.delete(`/users/${userId}`);
      toast.success('User deactivated');
      fetchData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to deactivate user'));
    }
  };

  const getRoleBadge = (role) => {
    // v3.21-1631 · Canonical external partner hierarchy — Principal /
    // Director 1 / Manager Director / Team Member.  Legacy slugs
    // remain accepted at the API boundary so we show the same label
    // during migration rollout.
    const colors = {
      company_principal:   'bg-rose-100 text-rose-800',
      company_admin:       'bg-rose-100 text-rose-800',
      company_director:    'bg-amber-100 text-amber-800',
      manager_director:    'bg-fuchsia-100 text-fuchsia-800',
      company_manager:     'bg-blue-100 text-blue-800',
      company_team_member: 'bg-slate-100 text-slate-700',
      vo_officer: 'bg-rose-100 text-rose-800',
    };
    const labels = {
      company_principal:   'Principal',
      company_admin:       'Principal',
      company_director:    'Director 1',
      manager_director:    'Manager Director',
      company_manager:     'Manager Director',
      company_team_member: 'Team Member',
      vo_officer:          'VO Officer',
    };
    return (
      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${colors[role] || 'bg-slate-100 text-slate-700'}`}>
        {labels[role] || role}
      </span>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-accent" />
      </div>
    );
  }

  if (!company) {
    return (
      <div className="text-center py-12">
        <Building2 className="h-12 w-12 text-slate-300 mx-auto mb-4" />
        <p className="text-slate-500">Company not found</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/companies')}>
          Back to Companies
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="company-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/companies')} data-testid="back-btn">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="font-heading text-2xl font-bold text-primary">{company.name}</h1>
          <p className="text-sm text-slate-500">Partner Financial Management</p>
        </div>
      </div>

      {/* Financial Summary Cards */}
      {financialSummary && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-red-600 uppercase tracking-wide">We Owe Partner</p>
                  <p className="text-2xl font-bold text-red-700 mt-1">
                    {formatCurrency(financialSummary.amount_we_owe)}
                  </p>
                </div>
                <div className="p-3 bg-red-200 rounded-full">
                  <TrendingDown className="h-5 w-5 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-green-600 uppercase tracking-wide">Partner Owes Us</p>
                  <p className="text-2xl font-bold text-green-700 mt-1">
                    {formatCurrency(financialSummary.amount_partner_owes)}
                  </p>
                </div>
                <div className="p-3 bg-green-200 rounded-full">
                  <TrendingUp className="h-5 w-5 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-blue-600 uppercase tracking-wide">Total Payments Sent</p>
                  <p className="text-2xl font-bold text-blue-700 mt-1">
                    {formatCurrency(financialSummary.total_payments_sent)}
                  </p>
                </div>
                <div className="p-3 bg-blue-200 rounded-full">
                  <Send className="h-5 w-5 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-purple-600 uppercase tracking-wide">Invoices Collected</p>
                  <p className="text-2xl font-bold text-purple-700 mt-1">
                    {formatCurrency(financialSummary.total_invoices_collected)}
                  </p>
                </div>
                <div className="p-3 bg-purple-200 rounded-full">
                  <CheckCircle className="h-5 w-5 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* v3.18-1245 PARTNER-FIN-001 — Auto-synchronised partner
          financial workspace. Read-only projection of every deal
          that reached Paid In Full + All Parties Settled. */}
      <PartnerFinancialSyncPanel companyId={id} />

      {/* v3.15-1187 ADMIN-001 — Company Workspace Foundation.
          v3.18-1345 A5/A6 — Placeholder Payroll and Documents tabs removed.
          Payroll continues to live at /payroll (+/payroll-engine). Company
          document management lives at /company-documents. Compensation
          module (administrative structure) stays. */}
      <Tabs defaultValue="overview" className="space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <TabsList className="flex flex-wrap h-auto">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="team" data-testid="tab-team">
              Team ({companyUsers.length})
            </TabsTrigger>
            {isInternalUser && (
              <TabsTrigger value="roles" data-testid="tab-roles">Roles</TabsTrigger>
            )}
            {isInternalUser && (
              <TabsTrigger value="compensation" data-testid="tab-compensation">Compensation</TabsTrigger>
            )}
            <TabsTrigger value="money-manager" data-testid="tab-money-manager">Money Manager</TabsTrigger>
            {isInternalUser && (
              <TabsTrigger value="communications" data-testid="tab-communications">Communications</TabsTrigger>
            )}
            <TabsTrigger value="intake-aliases" data-testid="tab-intake-aliases">Intake Aliases</TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings">Settings</TabsTrigger>
          </TabsList>

          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={fetchData}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Overview */}
        <TabsContent value="overview" className="space-y-4" data-testid="overview-tab-content">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-heading">Company Overview</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500">Company Name</p>
                <p className="font-medium text-slate-800">{company?.name || '—'}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500">Type</p>
                <p className="font-medium text-slate-800 capitalize">{company?.company_type || '—'}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500">Industry</p>
                <p className="font-medium text-slate-800">{company?.industry_category || '—'}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500">Status</p>
                <p className="font-medium text-slate-800 capitalize">{company?.status || 'active'}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500">Plan</p>
                <p className="font-medium text-slate-800">{company?.plan || '—'}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500">Team Size</p>
                <p className="font-medium text-slate-800">{companyUsers.length}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500">Clients</p>
                <p className="font-medium text-slate-800">{clients.length}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500">Created</p>
                <p className="font-medium text-slate-800">
                  {company?.created_at ? format(new Date(company.created_at), 'MMM d, yyyy') : '—'}
                </p>
              </div>
            </CardContent>
          </Card>
          <p className="text-xs text-slate-400 px-1">
            Financial summary cards are shown above the tab bar. Use the Money
            Manager tab to record wire payments and invoices.
          </p>
        </TabsContent>

        {/* Money Manager (hosts existing Wire Payments + Invoices) */}
        <TabsContent value="money-manager" className="space-y-4" data-testid="money-manager-tab-content">
          <Tabs defaultValue="payments" className="space-y-4">
            <TabsList>
              <TabsTrigger value="payments" className="flex items-center gap-2" data-testid="payments-tab">
                <Send className="h-4 w-4" />
                Wire Payments ({payments.length})
              </TabsTrigger>
              <TabsTrigger value="invoices" className="flex items-center gap-2" data-testid="invoices-tab">
                <Receipt className="h-4 w-4" />
                Invoices ({invoices.length})
              </TabsTrigger>
            </TabsList>

        {/* Payments Tab */}
        <TabsContent value="payments" className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-slate-500">Record wire payments sent to this partner</p>
            {isInternal() && (
              <Dialog open={isPaymentOpen} onOpenChange={setIsPaymentOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-accent hover:bg-gold-dark" data-testid="record-payment-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Record Payment
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Record Wire Payment</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreatePayment} className="space-y-4 mt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Amount *</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={newPayment.amount}
                          onChange={(e) => setNewPayment({ ...newPayment, amount: e.target.value })}
                          required
                          placeholder="0.00"
                          data-testid="payment-amount"
                        />
                      </div>
                      <div>
                        <Label>Wire Date *</Label>
                        <Input
                          type="date"
                          value={newPayment.wire_date}
                          onChange={(e) => setNewPayment({ ...newPayment, wire_date: e.target.value })}
                          required
                          data-testid="payment-date"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Wire Reference</Label>
                        <Input
                          value={newPayment.wire_reference}
                          onChange={(e) => setNewPayment({ ...newPayment, wire_reference: e.target.value })}
                          placeholder="WIRE-001"
                          data-testid="payment-reference"
                        />
                      </div>
                      <div>
                        <Label>Bank Name</Label>
                        <Input
                          value={newPayment.bank_name}
                          onChange={(e) => setNewPayment({ ...newPayment, bank_name: e.target.value })}
                          placeholder="Chase Bank"
                          data-testid="payment-bank"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Related Client/Deal (optional)</Label>
                      <Select
                        value={newPayment.client_id}
                        onValueChange={(value) => setNewPayment({ ...newPayment, client_id: value === 'none' ? '' : value })}
                      >
                        <SelectTrigger data-testid="payment-client">
                          <SelectValue placeholder="Select a client..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          {clients.map((client) => (
                            <SelectItem key={client.id} value={client.id}>
                              {client.full_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Notes</Label>
                      <Textarea
                        value={newPayment.notes}
                        onChange={(e) => setNewPayment({ ...newPayment, notes: e.target.value })}
                        placeholder="Additional notes..."
                        data-testid="payment-notes"
                      />
                    </div>
                    <div className="flex justify-end gap-3 pt-4">
                      <Button type="button" variant="outline" onClick={() => setIsPaymentOpen(false)}>
                        Cancel
                      </Button>
                      <Button type="submit" className="bg-accent hover:bg-gold-dark" disabled={saving} data-testid="submit-payment">
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Record Payment'}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>

          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Wire Reference</TableHead>
                  <TableHead>Bank</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Notes</TableHead>
                  <TableHead>Recorded By</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                      No payments recorded yet
                    </TableCell>
                  </TableRow>
                ) : (
                  payments.map((payment) => (
                    <TableRow
                      key={payment.id}
                      data-testid={`payment-row-${payment.id}`}
                      className="cursor-pointer hover:bg-slate-50 transition-colors"
                      onClick={() => setViewPayment(payment)}
                    >
                      <TableCell>{payment.wire_date}</TableCell>
                      <TableCell className="font-semibold text-green-600">
                        {formatCurrency(payment.amount)}
                      </TableCell>
                      <TableCell className="font-medium text-blue-600">{payment.wire_reference || '-'}</TableCell>
                      <TableCell>{payment.bank_name || '-'}</TableCell>
                      <TableCell>{payment.client_name || '-'}</TableCell>
                      <TableCell className="max-w-[200px] truncate">{payment.notes || '-'}</TableCell>
                      <TableCell className="text-sm text-slate-500">{payment.created_by_name}</TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setViewPayment(payment)}
                            className="text-blue-600 hover:text-blue-700"
                            data-testid={`view-payment-${payment.id}`}
                            title="View wire details"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          {isInternal() && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeletePayment(payment.id)}
                              className="text-red-600 hover:text-red-700"
                              data-testid={`delete-payment-${payment.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Invoices Tab */}
        <TabsContent value="invoices" className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-slate-500">Track invoices for money this partner owes us</p>
            {isInternal() && (
              <Dialog open={isInvoiceOpen} onOpenChange={setIsInvoiceOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-accent hover:bg-gold-dark" data-testid="create-invoice-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Create Invoice
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Create Invoice</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateInvoice} className="space-y-4 mt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Invoice Number *</Label>
                        <Input
                          value={newInvoice.invoice_number}
                          onChange={(e) => setNewInvoice({ ...newInvoice, invoice_number: e.target.value })}
                          required
                          placeholder="INV-2026-001"
                          data-testid="invoice-number"
                        />
                      </div>
                      <div>
                        <Label>Amount *</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={newInvoice.amount}
                          onChange={(e) => setNewInvoice({ ...newInvoice, amount: e.target.value })}
                          required
                          placeholder="0.00"
                          data-testid="invoice-amount"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Invoice Date *</Label>
                        <Input
                          type="date"
                          value={newInvoice.invoice_date}
                          onChange={(e) => setNewInvoice({ ...newInvoice, invoice_date: e.target.value })}
                          required
                          data-testid="invoice-date"
                        />
                      </div>
                      <div>
                        <Label>Due Date</Label>
                        <Input
                          type="date"
                          value={newInvoice.due_date}
                          onChange={(e) => setNewInvoice({ ...newInvoice, due_date: e.target.value })}
                          data-testid="invoice-due-date"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Related Client/Deal (optional)</Label>
                      <Select
                        value={newInvoice.client_id}
                        onValueChange={(value) => setNewInvoice({ ...newInvoice, client_id: value === 'none' ? '' : value })}
                      >
                        <SelectTrigger data-testid="invoice-client">
                          <SelectValue placeholder="Select a client..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          {clients.map((client) => (
                            <SelectItem key={client.id} value={client.id}>
                              {client.full_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Notes</Label>
                      <Textarea
                        value={newInvoice.notes}
                        onChange={(e) => setNewInvoice({ ...newInvoice, notes: e.target.value })}
                        placeholder="Additional notes..."
                        data-testid="invoice-notes"
                      />
                    </div>
                    <div className="flex justify-end gap-3 pt-4">
                      <Button type="button" variant="outline" onClick={() => setIsInvoiceOpen(false)}>
                        Cancel
                      </Button>
                      <Button type="submit" className="bg-accent hover:bg-gold-dark" disabled={saving} data-testid="submit-invoice">
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create Invoice'}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>

          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Invoice #</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Invoice Date</TableHead>
                  <TableHead>Due Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Paid Date</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                      No invoices created yet
                    </TableCell>
                  </TableRow>
                ) : (
                  invoices.map((invoice) => (
                    <TableRow
                      key={invoice.id}
                      data-testid={`invoice-row-${invoice.id}`}
                      className="cursor-pointer hover:bg-slate-50 transition-colors"
                      onClick={() => setViewInvoice(invoice)}
                    >
                      <TableCell className="font-medium text-blue-600">{invoice.invoice_number}</TableCell>
                      <TableCell className="font-semibold">{formatCurrency(invoice.amount)}</TableCell>
                      <TableCell>{invoice.invoice_date}</TableCell>
                      <TableCell>{invoice.due_date || '-'}</TableCell>
                      <TableCell>
                        <Badge 
                          variant={invoice.status === 'paid' ? 'default' : 'secondary'}
                          className={invoice.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}
                        >
                          {invoice.status === 'paid' ? (
                            <><CheckCircle className="h-3 w-3 mr-1" /> Paid</>
                          ) : (
                            <><Clock className="h-3 w-3 mr-1" /> Pending</>
                          )}
                        </Badge>
                      </TableCell>
                      <TableCell>{invoice.paid_date || '-'}</TableCell>
                      <TableCell>{invoice.client_name || '-'}</TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setViewInvoice(invoice)}
                            className="text-blue-600 hover:text-blue-700"
                            data-testid={`view-invoice-${invoice.id}`}
                            title="View invoice details"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          {invoice.status === 'pending' && isInternal() && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleMarkInvoicePaid(invoice.id)}
                              className="text-green-600 hover:text-green-700"
                              data-testid={`mark-paid-${invoice.id}`}
                            >
                              <CheckCircle className="h-4 w-4 mr-1" />
                              Mark Paid
                            </Button>
                          )}
                          {isInternal() && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteInvoice(invoice.id)}
                              className="text-red-600 hover:text-red-700"
                              data-testid={`delete-invoice-${invoice.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
          </Tabs>
        </TabsContent>

        {/* Team Tab (v3.15-1187 ADMIN-001) */}
        <TabsContent value="team" className="space-y-4" data-testid="team-tab-content">
          <div className="flex justify-between items-center">
            <p className="text-sm text-slate-500">Manage team members for {company?.name}</p>
            <Dialog open={isUserDialogOpen} onOpenChange={setIsUserDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-accent hover:bg-gold-dark" onClick={openAddUser} data-testid="add-user-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Add User
                </Button>
              </DialogTrigger>
              <DialogContent className="max-h-[92vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingUser ? 'Edit Employee' : 'Add New Employee'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSaveUser} className="space-y-4 mt-2">
                  <div>
                    <label className="text-sm font-medium text-slate-700">Full Name *</label>
                    <Input
                      value={userForm.full_name}
                      onChange={e => setUserForm(prev => ({ ...prev, full_name: e.target.value }))}
                      placeholder="Enter full name"
                      data-testid="user-name-input"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700">Email *</label>
                    <Input
                      type="email"
                      value={userForm.email}
                      onChange={e => setUserForm(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="user@company.com"
                      data-testid="user-email-input"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700">Phone</label>
                    <Input
                      value={userForm.phone}
                      onChange={e => setUserForm(prev => ({ ...prev, phone: e.target.value }))}
                      placeholder="(555) 123-4567"
                      data-testid="user-phone-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700">Primary Role</label>
                    <select
                      className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                      value={userForm.role}
                      onChange={e => setUserForm(prev => ({ ...prev, role: e.target.value }))}
                      data-testid="user-role-select"
                    >
                      {EXTERNAL_ROLES.map(r => (
                        // v3.21-1631 · Principal is owner-level and only
                        // selectable by PTC internal admins.  Partner
                        // users cannot self-promote to Principal.
                        <option key={r.value} value={r.value}
                                disabled={r.principalGated && !isAdmin}>
                          {r.label}{r.principalGated && !isAdmin ? ' (PTC admin only)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  {/* v3.15-1188 ADMIN-002 — Company Assignment.
                      Only shown while editing (creation always assigns to the
                      current company). Reassignment writes a company_history
                      row on the user document. */}
                  {editingUser && (
                    <div>
                      <label className="text-sm font-medium text-slate-700">Company Assignment</label>
                      <select
                        className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                        value={userForm.company_id || ''}
                        onChange={e => setUserForm(prev => ({ ...prev, company_id: e.target.value }))}
                        data-testid="user-company-select"
                      >
                        {allCompanies.map(c => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </select>
                      {userForm.company_id && userForm.company_id !== (editingUser?.company_id || id) && (
                        <p className="text-xs text-amber-600 mt-1">
                          Reassigning this employee will move them out of {company?.name || 'this company'}.
                        </p>
                      )}
                    </div>
                  )}
                  {/* v3.15-1188 ADMIN-002 — Optional audit note stamped on the
                      role_history entry when the primary role actually changes. */}
                  {editingUser && userForm.role !== editingUser.role && (
                    <div>
                      <label className="text-sm font-medium text-slate-700">Role change note (optional)</label>
                      <Input
                        value={userForm.role_change_note}
                        onChange={e => setUserForm(prev => ({ ...prev, role_change_note: e.target.value }))}
                        placeholder="e.g. Promoted to Manager after Q1 review"
                        data-testid="user-role-change-note"
                      />
                    </div>
                  )}
                  {/* v3.15-1189 ADMIN-003 — Additional (concurrent) roles.
                      Multiple roles can be active on the same employee, each
                      with its own Level, Effective Date, Active/Inactive
                      status, and optional note. Primary Role stays above.
                      NO compensation, deal ownership, or payroll wiring — this
                      is the assignment surface only. */}
                  {editingUser && (
                    <div className="border-t border-slate-200 pt-4">
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-sm font-medium text-slate-700">
                          Additional Roles ({(userForm.additional_roles || []).length})
                        </label>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          data-testid="add-additional-role-btn"
                          onClick={() => setUserForm(prev => ({
                            ...prev,
                            additional_roles: [
                              ...(prev.additional_roles || []),
                              {
                                role: 'company_team_member',
                                level: 1,
                                effective_date: format(new Date(), 'yyyy-MM-dd'),
                                is_active: true,
                                note: '',
                              },
                            ],
                          }))}
                        >
                          <Plus className="h-3.5 w-3.5 mr-1" />
                          Add Role
                        </Button>
                      </div>
                      {(userForm.additional_roles || []).length === 0 ? (
                        <p className="text-xs text-slate-400">No additional roles.</p>
                      ) : (
                        <div className="space-y-2">
                          {(userForm.additional_roles || []).map((r, idx) => (
                            <div
                              key={idx}
                              className="border border-slate-200 rounded p-3 space-y-2 bg-slate-50/50"
                              data-testid={`additional-role-row-${idx}`}
                            >
                              <div className="grid grid-cols-2 gap-2">
                                <div>
                                  <label className="text-[11px] uppercase tracking-wide text-slate-500">Role</label>
                                  <select
                                    className="w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm"
                                    value={r.role}
                                    data-testid={`additional-role-select-${idx}`}
                                    onChange={e => setUserForm(prev => ({
                                      ...prev,
                                      additional_roles: prev.additional_roles.map((x, i) => i === idx ? { ...x, role: e.target.value } : x),
                                    }))}
                                  >
                                    {EXTERNAL_ROLES.map(opt => (
                                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                  </select>
                                </div>
                                <div>
                                  <label className="text-[11px] uppercase tracking-wide text-slate-500">Level</label>
                                  <Input
                                    type="number"
                                    min={1}
                                    value={r.level ?? 1}
                                    data-testid={`additional-role-level-${idx}`}
                                    onChange={e => setUserForm(prev => ({
                                      ...prev,
                                      additional_roles: prev.additional_roles.map((x, i) => i === idx ? { ...x, level: e.target.value } : x),
                                    }))}
                                  />
                                </div>
                                <div>
                                  <label className="text-[11px] uppercase tracking-wide text-slate-500">Effective Date</label>
                                  <Input
                                    type="date"
                                    value={r.effective_date || ''}
                                    data-testid={`additional-role-date-${idx}`}
                                    onChange={e => setUserForm(prev => ({
                                      ...prev,
                                      additional_roles: prev.additional_roles.map((x, i) => i === idx ? { ...x, effective_date: e.target.value } : x),
                                    }))}
                                  />
                                </div>
                                <div className="flex items-end">
                                  <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer" data-testid={`additional-role-active-toggle-${idx}`}>
                                    <input
                                      type="checkbox"
                                      checked={r.is_active !== false}
                                      onChange={e => setUserForm(prev => ({
                                        ...prev,
                                        additional_roles: prev.additional_roles.map((x, i) => i === idx ? { ...x, is_active: e.target.checked } : x),
                                      }))}
                                      className="h-4 w-4"
                                    />
                                    {r.is_active !== false ? 'Active' : 'Inactive'}
                                  </label>
                                </div>
                              </div>
                              <div>
                                <label className="text-[11px] uppercase tracking-wide text-slate-500">Note (optional)</label>
                                <Input
                                  value={r.note || ''}
                                  placeholder="Context for this assignment"
                                  data-testid={`additional-role-note-${idx}`}
                                  onChange={e => setUserForm(prev => ({
                                    ...prev,
                                    additional_roles: prev.additional_roles.map((x, i) => i === idx ? { ...x, note: e.target.value } : x),
                                  }))}
                                />
                              </div>
                              {/* v3.15-1193 COMP-002 — per-role Compensation Plan link.
                                  Assignment only. Options are limited to plans
                                  belonging to the employee's assigned Company. */}
                              <div>
                                <label className="text-[11px] uppercase tracking-wide text-slate-500">Compensation Plan (optional)</label>
                                <select
                                  className="w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm"
                                  value={r.compensation_plan_id || ''}
                                  data-testid={`additional-role-comp-plan-${idx}`}
                                  onChange={e => setUserForm(prev => ({
                                    ...prev,
                                    additional_roles: prev.additional_roles.map((x, i) => i === idx ? { ...x, compensation_plan_id: e.target.value } : x),
                                  }))}
                                >
                                  <option value="">— None —</option>
                                  {employeePlans.map(p => (
                                    <option key={p.id} value={p.id}>
                                      {p.role} · L{p.level}{p.effective_date ? ` · from ${p.effective_date}` : ''}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div className="flex justify-between items-center pt-1">
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  data-testid={`promote-additional-role-${idx}`}
                                  disabled={r.role === userForm.role}
                                  title="Swap this role with the Primary Role"
                                  onClick={() => setUserForm(prev => {
                                    const oldPrimary = prev.role;
                                    const promoted = prev.additional_roles[idx];
                                    // Replace the entry with the (previously) primary role, at Level 1.
                                    const newList = prev.additional_roles.map((x, i) => i === idx ? {
                                      ...x,
                                      role: oldPrimary,
                                      level: 1,
                                      effective_date: x.effective_date || format(new Date(), 'yyyy-MM-dd'),
                                      is_active: true,
                                      note: `Demoted from Primary on ${format(new Date(), 'yyyy-MM-dd')}`,
                                    } : x);
                                    return {
                                      ...prev,
                                      role: promoted.role,
                                      additional_roles: newList,
                                    };
                                  })}
                                >
                                  Set as Primary
                                </Button>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-600 hover:text-red-700"
                                  data-testid={`remove-additional-role-${idx}`}
                                  onClick={() => setUserForm(prev => ({
                                    ...prev,
                                    additional_roles: prev.additional_roles.filter((_, i) => i !== idx),
                                  }))}
                                >
                                  <Trash2 className="h-3.5 w-3.5 mr-1" />
                                  Remove
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  {/* v3.15-1193 COMP-002 — Top-level Compensation Plans multi-select.
                      Assignment only, no calc logic. Options are Company-scoped
                      to userForm.company_id (the employee's assigned Company).
                      Level 2+ plans work architecturally because each plan
                      carries its own `level` and this list is just plan ids. */}
                  {editingUser && (
                    <div className="border-t border-slate-200 pt-4">
                      <label className="text-sm font-medium text-slate-700 block mb-2">
                        Compensation Plans (Company-scoped)
                      </label>
                      {employeePlans.length === 0 ? (
                        <p className="text-xs text-slate-400" data-testid="employee-comp-plans-empty">
                          No active compensation plans defined for this Company. Create one under Company &rarr; Compensation.
                        </p>
                      ) : (
                        <div className="space-y-1" data-testid="employee-comp-plans-list">
                          {employeePlans.map(p => {
                            const checked = (userForm.compensation_plan_ids || []).includes(p.id);
                            return (
                              <label
                                key={p.id}
                                className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer border border-slate-200 rounded px-2 py-1.5 hover:bg-slate-50"
                                data-testid={`employee-comp-plan-option-${p.id}`}
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={e => setUserForm(prev => {
                                    const cur = new Set(prev.compensation_plan_ids || []);
                                    if (e.target.checked) cur.add(p.id);
                                    else cur.delete(p.id);
                                    return { ...prev, compensation_plan_ids: Array.from(cur) };
                                  })}
                                  className="h-4 w-4"
                                />
                                <span className="font-medium">{p.role}</span>
                                <span className="text-[11px] text-slate-500">
                                  L{p.level}{p.effective_date ? ` · from ${p.effective_date}` : ''}{p.expiration_date ? ` · until ${p.expiration_date}` : ''}
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}

                  <div>
                    <label className="text-sm font-medium text-slate-700">
                      {editingUser ? 'New Password (leave blank to keep current)' : 'Password *'}
                    </label>
                    <Input
                      type="password"
                      value={userForm.password}
                      onChange={e => setUserForm(prev => ({ ...prev, password: e.target.value }))}
                      placeholder={editingUser ? 'Leave blank to keep current' : 'Enter password'}
                      data-testid="user-password-input"
                      required={!editingUser}
                    />
                  </div>
                  <div className="flex justify-end gap-2 pt-2">
                    <Button type="button" variant="outline" onClick={() => setIsUserDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={saving} className="bg-accent hover:bg-gold-dark" data-testid="save-user-btn">
                      {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                      {editingUser ? 'Update Employee' : 'Create Employee'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Primary Role</TableHead>
                  <TableHead>Additional Roles</TableHead>
                  <TableHead>Permission Group</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>Credentials</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {companyUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-slate-400 py-8">
                      No team members yet. Click &quot;Add User&quot; to create one.
                    </TableCell>
                  </TableRow>
                ) : (
                  companyUsers.map(user => {
                    const isActive = user.is_active !== false;
                    const additional = Array.isArray(user.additional_roles) ? user.additional_roles : [];
                    const credentialsSent = !!user.credentials_sent;
                    // v3.21-1616 · Resolve the composite account status
                    // into ONE operator-facing label.  Priority order:
                    // Suspended/Terminated → Pending Approval →
                    // Pending Setup (no password_hash / never signed in)
                    // → Inactive → Active.
                    const _u = user || {};
                    let status = 'Active';
                    let statusTone = 'bg-emerald-100 text-emerald-700 border-emerald-200';
                    if (_u.suspended || _u.suspended_at) {
                        status = 'Suspended'; statusTone = 'bg-orange-100 text-orange-800 border-orange-200';
                    } else if (_u.terminated || _u.terminated_at) {
                        status = 'Terminated'; statusTone = 'bg-rose-100 text-rose-800 border-rose-200';
                    } else if ((_u.approval_status || 'approved') === 'pending') {
                        status = 'Pending Approval'; statusTone = 'bg-amber-100 text-amber-800 border-amber-200';
                    } else if ((_u.approval_status || 'approved') === 'rejected') {
                        status = 'Rejected'; statusTone = 'bg-rose-100 text-rose-800 border-rose-200';
                    } else if (_u.is_active === false || (_u.user_status && _u.user_status !== 'active')) {
                        status = 'Inactive'; statusTone = 'bg-slate-200 text-slate-700 border-slate-300';
                    } else if (isActive && !_u.password_hash_present && _u.must_change_password === true && !_u.last_login) {
                        // Best-effort "Pending Setup" — user is active but
                        // has never signed in and still has the initial
                        // must_change_password flag.  password_hash is
                        // never sent to the browser so we soft-detect
                        // via last_login.
                        status = 'Pending Setup'; statusTone = 'bg-sky-100 text-sky-800 border-sky-200';
                    }
                    return (
                      <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                        <TableCell className="font-medium">
                          <div>{user.full_name}</div>
                          <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                            <Mail className="h-3 w-3" />
                            {user.email || '—'}
                          </div>
                        </TableCell>
                        <TableCell>
                          <span
                            className={`text-xs px-2 py-0.5 rounded-full font-medium border ${statusTone}`}
                            data-testid={`user-status-${user.id}`}
                          >
                            {status}
                          </span>
                        </TableCell>
                        <TableCell>{getRoleBadge(user.role)}</TableCell>
                        <TableCell>
                          {additional.length === 0 ? (
                            <span className="text-xs text-slate-400">—</span>
                          ) : (
                            <div className="flex flex-wrap gap-1">
                              {additional.map((r, i) => {
                                const active = r?.is_active !== false;
                                return (
                                  <span
                                    key={`${user.id}-add-${i}`}
                                    className={
                                      active
                                        ? "text-[11px] px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700 font-medium border border-indigo-100"
                                        : "text-[11px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500 line-through border border-slate-200"
                                    }
                                    title={r?.effective_date ? `Effective ${r.effective_date}${r?.note ? ` — ${r.note}` : ''}` : (r?.note || '')}
                                  >
                                    {(r?.role || 'role').toString()}
                                    {r?.level != null ? ` · L${r.level}` : ''}
                                  </span>
                                );
                              })}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <span className="text-xs text-slate-600">{user.role || '—'}</span>
                        </TableCell>
                        <TableCell>
                          <span className="text-xs text-slate-600">{company?.name || '—'}</span>
                        </TableCell>
                        <TableCell>
                          {credentialsSent ? (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 font-medium inline-flex items-center gap-1" data-testid={`user-creds-status-${user.id}`}>
                              <MailCheck className="h-3 w-3" /> Sent
                            </span>
                          ) : (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-medium" data-testid={`user-creds-status-${user.id}`}>
                              Not sent
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            {/* View */}
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setViewingUser(user)}
                              data-testid={`view-user-${user.id}`}
                              title="View team member"
                            >
                              <Eye className="h-4 w-4 text-slate-500" />
                            </Button>
                            {/* Edit */}
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openEditUser(user)}
                              data-testid={`edit-user-${user.id}`}
                              title="Edit team member"
                            >
                              <Edit className="h-4 w-4 text-slate-500" />
                            </Button>
                            {/* Send / Resend Credentials */}
                            {credentialsSent ? (
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    disabled={sendingCredentialsIds.has(user.id) || !user.email}
                                    data-testid={`send-credentials-user-${user.id}`}
                                    title="Resend welcome credentials"
                                  >
                                    {sendingCredentialsIds.has(user.id) ? (
                                      <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                                    ) : (
                                      <MailCheck className="h-4 w-4 text-blue-600" />
                                    )}
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent data-testid={`resend-credentials-confirm-${user.id}`}>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Resend Credentials</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      This user has previously been sent login credentials.
                                      Would you like to send them again?
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel data-testid={`resend-credentials-cancel-${user.id}`}>Cancel</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => handleSendCredentials(user.id)}
                                      data-testid={`resend-credentials-confirm-btn-${user.id}`}
                                    >
                                      Send Again
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            ) : (
                              <Button
                                variant="ghost"
                                size="icon"
                                disabled={sendingCredentialsIds.has(user.id) || !user.email}
                                onClick={() => handleSendCredentials(user.id)}
                                data-testid={`send-credentials-user-${user.id}`}
                                title={user.email ? 'Send welcome credentials' : 'No email on file'}
                              >
                                {sendingCredentialsIds.has(user.id) ? (
                                  <Loader2 className="h-4 w-4 animate-spin text-emerald-700" />
                                ) : (
                                  <Mail className="h-4 w-4 text-emerald-700" />
                                )}
                              </Button>
                            )}
                            {/* v3.21-1616 · Activate / Deactivate Account.
                                Resolved from the composite status computed
                                above.  Backend enforces same-company /
                                partner-authority / suspend-guard. */}
                            {status === 'Active' ? (
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    data-testid={`deactivate-account-${user.id}`}
                                    title="Deactivate account"
                                  >
                                    <Ban className="h-4 w-4 text-amber-600" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Deactivate Account</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Deactivate <strong>{user.full_name}</strong>&apos;s Hub access?
                                      They will no longer be able to sign in.
                                      Their record, activity, and audit history are preserved.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => handleToggleLogin(user)}
                                      className="bg-amber-600 hover:bg-amber-700"
                                      data-testid={`confirm-deactivate-account-${user.id}`}
                                    >
                                      Deactivate Account
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            ) : (status === 'Suspended' || status === 'Terminated') ? (
                              <Button
                                variant="ghost"
                                size="icon"
                                disabled
                                data-testid={`account-locked-${user.id}`}
                                title={`Account is ${status.toLowerCase()} — contact PTC admin`}
                              >
                                <Ban className="h-4 w-4 text-rose-400" />
                              </Button>
                            ) : (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleToggleLogin(user)}
                                data-testid={`activate-account-${user.id}`}
                                title="Activate account"
                              >
                                <Power className="h-4 w-4 text-emerald-700" />
                              </Button>
                            )}
                            {/* v3.18-1345 A4 — removed placeholder Money Manager
                                and Payroll icon buttons per operational-cleanup rule
                                ("do not expose unfinished functionality"). Re-add
                                once /money-manager?employee={id} and /payroll?employee={id}
                                are implemented. */}
                            {/* v3.15-1188 ADMIN-002 — Role History timeline */}
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setHistoryUser(user)}
                              data-testid={`role-history-${user.id}`}
                              title="View role history"
                            >
                              <Clock className="h-4 w-4 text-slate-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Roles Tab — v3.15-1187 ADMIN-001 catalog display (read-only).
            v3.21-1613 — Internal PTC users only. */}
        {isInternalUser && (
        <TabsContent value="roles" className="space-y-4" data-testid="roles-tab-content">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-heading flex items-center gap-2">
                <Shield className="h-4 w-4 text-slate-600" />
                Available Roles
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm">
              {(() => {
                const key = resolveCompanyCatalogKey(company);
                const bucket = COMPANY_ROLE_CATALOG[key];
                return (
                  <div>
                    <p className="text-xs uppercase tracking-wide text-slate-500 mb-2">
                      Catalog for this company
                    </p>
                    <div className="flex items-center gap-2 mb-3">
                      <Badge variant="secondary" className="bg-slate-100 text-slate-700">
                        {bucket.label}
                      </Badge>
                    </div>
                    <ul className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="roles-catalog-list">
                      {bucket.roles.map(r => (
                        <li key={r} className="flex items-center gap-2 px-3 py-2 rounded border border-slate-200 bg-white">
                          <Shield className="h-3.5 w-3.5 text-slate-500" />
                          <span className="font-medium text-slate-700">{r}</span>
                          <span className="ml-auto text-[11px] text-slate-400 uppercase tracking-wide">Level 1</span>
                        </li>
                      ))}
                    </ul>
                    <p className="text-xs text-slate-400 mt-4">
                      Multi-role assignment (multiple simultaneous roles per team member) is architecturally
                      supported. Assignment UI + compensation are reserved for a future task.
                    </p>
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        </TabsContent>
        )}

        {/* v3.15-1192 COMP-001 — Compensation module (administrative only).
            Plans belong to the company. Employees reference plans via
            `compensation_plan_ids`. NO calculation logic here.
            v3.21-1613 — Internal PTC users only. */}
        {isInternalUser && (
        <TabsContent value="compensation" className="space-y-4" data-testid="compensation-tab-content">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg font-heading flex items-center gap-2">
                  <Briefcase className="h-4 w-4 text-slate-600" />
                  Compensation Plans
                </CardTitle>
                <p className="text-xs text-slate-500 mt-1">
                  Administrative structure only. No calculations or payroll wiring in this task.
                </p>
              </div>
              <Button
                size="sm"
                onClick={openAddPlan}
                className="bg-accent hover:bg-gold-dark"
                data-testid="add-compensation-plan-btn"
              >
                <Plus className="h-4 w-4 mr-1" />
                New Plan
              </Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Role</TableHead>
                    <TableHead>Level</TableHead>
                    <TableHead>Effective Date</TableHead>
                    <TableHead>Expiration Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Notes</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {compensationPlans.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-slate-400 py-8" data-testid="compensation-plans-empty">
                        No compensation plans yet. Click &quot;New Plan&quot; to create one.
                      </TableCell>
                    </TableRow>
                  ) : (
                    compensationPlans.map(plan => (
                      <TableRow key={plan.id} data-testid={`compensation-plan-row-${plan.id}`}>
                        <TableCell className="font-medium">{plan.role}</TableCell>
                        <TableCell>L{plan.level ?? 1}</TableCell>
                        <TableCell className="text-sm text-slate-600">{plan.effective_date || '—'}</TableCell>
                        <TableCell className="text-sm text-slate-600">{plan.expiration_date || '—'}</TableCell>
                        <TableCell>
                          {plan.status === 'active' ? (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium" data-testid={`compensation-plan-status-${plan.id}`}>
                              Active
                            </span>
                          ) : (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-slate-200 text-slate-600 font-medium" data-testid={`compensation-plan-status-${plan.id}`}>
                              Inactive
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-xs text-slate-500 max-w-xs truncate" title={plan.notes || ''}>
                          {plan.notes || '—'}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openRulesForPlan(plan)}
                              data-testid={`open-rules-plan-${plan.id}`}
                              title="Manage compensation rules"
                            >
                              <Shield className="h-4 w-4 mr-1 text-slate-500" />
                              Rules ({(plan.rules || []).length})
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openEditPlan(plan)}
                              data-testid={`edit-compensation-plan-${plan.id}`}
                              title="Edit plan"
                            >
                              <Edit className="h-4 w-4 text-slate-500" />
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  data-testid={`delete-compensation-plan-${plan.id}`}
                                  title="Delete plan"
                                >
                                  <Trash2 className="h-4 w-4 text-red-500" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete Compensation Plan</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Are you sure you want to delete the <strong>{plan.role} · L{plan.level}</strong> plan?
                                    This cannot be undone.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => handleDeletePlan(plan.id)}
                                    className="bg-red-600 hover:bg-red-700"
                                    data-testid={`confirm-delete-compensation-plan-${plan.id}`}
                                  >
                                    Delete
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
        )}

        {/* v3.18-1345 A5/A6 — Payroll and Documents TabsContent removed.
            Users are directed to /payroll and /company-documents which are
            the working implementations. */}

        {/* Settings — read-only company info (edit lives on the Companies list). */}
        {/* v3.18-1385 GMAIL-005 — Communications tab
            v3.21-1613 — Internal PTC users only. */}
        {isInternalUser && (
        <TabsContent value="communications" className="space-y-4" data-testid="communications-tab-content">
          <CompanyCommunicationsPanel companyId={id} />
        </TabsContent>
        )}

        {/* v3.18-1385 GMAIL-005 — Intake Aliases native tab */}
        <TabsContent value="intake-aliases" className="space-y-4" data-testid="intake-aliases-tab-content">
          <IntakeAliasesSection companyId={id} />
        </TabsContent>

        <TabsContent value="settings" className="space-y-4" data-testid="settings-tab-content">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-heading flex items-center gap-2">
                <SettingsIcon className="h-4 w-4 text-slate-600" />
                Company Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-500">Name</p>
                  <p className="font-medium">{company?.name || '—'}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-500">Type</p>
                  <p className="font-medium capitalize">{company?.company_type || '—'}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-500">Industry</p>
                  <p className="font-medium">{company?.industry_category || '—'}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-500">Plan</p>
                  <p className="font-medium">{company?.plan || '—'}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-500">Status</p>
                  <p className="font-medium capitalize">{company?.status || 'active'}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-500">Created</p>
                  <p className="font-medium">
                    {company?.created_at ? format(new Date(company.created_at), 'MMM d, yyyy') : '—'}
                  </p>
                </div>
              </div>
              <p className="text-xs text-slate-400 pt-2 border-t border-slate-100">
                To edit company profile fields, use the &quot;Edit&quot; action on the Companies list.
                Deeper settings (branding, permission groups, notification preferences) are
                reserved for a future task.
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* v3.15-1187 ADMIN-001 — Team member "View" modal (read-only summary). */}
      <Dialog open={!!viewingUser} onOpenChange={(o) => { if (!o) setViewingUser(null); }}>
        <DialogContent className="max-w-md" data-testid="view-user-modal">
          <DialogHeader>
            <DialogTitle>Team Member</DialogTitle>
          </DialogHeader>
          {viewingUser && (
            <div className="space-y-3 text-sm">
              <div>
                <p className="text-[11px] uppercase tracking-wide text-slate-500">Name</p>
                <p className="font-medium">{viewingUser.full_name || '—'}</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-slate-500">Email</p>
                  <p className="font-medium break-all">{viewingUser.email || '—'}</p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-slate-500">Phone</p>
                  <p className="font-medium">{viewingUser.phone || '—'}</p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-slate-500">Primary Role</p>
                  <p className="font-medium">{viewingUser.role || '—'}</p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-slate-500">Status</p>
                  <p className="font-medium">{viewingUser.is_active === false ? 'Disabled' : 'Active'}</p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-slate-500">Credentials</p>
                  <p className="font-medium">{viewingUser.credentials_sent ? 'Sent' : 'Not sent'}</p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-slate-500">Company</p>
                  <p className="font-medium">{company?.name || '—'}</p>
                </div>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Additional Roles</p>
                {(viewingUser.additional_roles || []).length === 0 ? (
                  <p className="text-xs text-slate-400">—</p>
                ) : (
                  <div className="flex flex-wrap gap-1">
                    {(viewingUser.additional_roles || []).map((r, i) => (
                      <span key={i} className="text-[11px] px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100">
                        {(r?.role || 'role').toString()}{r?.level != null ? ` · L${r.level}` : ''}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* v3.15-1192 COMP-001 — Compensation plan Add/Edit dialog. */}
      <Dialog open={isPlanDialogOpen} onOpenChange={setIsPlanDialogOpen}>
        <DialogContent data-testid="compensation-plan-dialog">
          <DialogHeader>
            <DialogTitle>{editingPlan ? 'Edit Compensation Plan' : 'New Compensation Plan'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSavePlan} className="space-y-3 mt-2 text-sm">
            <div>
              <label className="text-xs uppercase tracking-wide text-slate-500">Role *</label>
              <Input
                value={planForm.role}
                onChange={e => setPlanForm(prev => ({ ...prev, role: e.target.value }))}
                placeholder="e.g. Sales Rep, Manager, Presenter, Application Concierge"
                data-testid="compensation-plan-role-input"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs uppercase tracking-wide text-slate-500">Level</label>
                <Input
                  type="number"
                  min={1}
                  value={planForm.level}
                  onChange={e => setPlanForm(prev => ({ ...prev, level: e.target.value }))}
                  data-testid="compensation-plan-level-input"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wide text-slate-500">Status</label>
                <select
                  className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                  value={planForm.status}
                  onChange={e => setPlanForm(prev => ({ ...prev, status: e.target.value }))}
                  data-testid="compensation-plan-status-input"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wide text-slate-500">Effective Date</label>
                <Input
                  type="date"
                  value={planForm.effective_date}
                  onChange={e => setPlanForm(prev => ({ ...prev, effective_date: e.target.value }))}
                  data-testid="compensation-plan-effective-date"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wide text-slate-500">Expiration Date</label>
                <Input
                  type="date"
                  value={planForm.expiration_date}
                  onChange={e => setPlanForm(prev => ({ ...prev, expiration_date: e.target.value }))}
                  data-testid="compensation-plan-expiration-date"
                />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wide text-slate-500">Notes</label>
              <Input
                value={planForm.notes}
                onChange={e => setPlanForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Any context about this plan (no formulas)"
                data-testid="compensation-plan-notes"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setIsPlanDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={savingPlan} className="bg-accent hover:bg-gold-dark" data-testid="save-compensation-plan-btn">
                {savingPlan ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {editingPlan ? 'Update Plan' : 'Create Plan'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* v3.15-1194 COMP-003 — Compensation Rule dialog (storage only).
          Rules belong to a Compensation Plan. STORAGE ONLY — nothing in this
          codebase evaluates or executes these rules. */}
      <Dialog open={isRulesDialogOpen} onOpenChange={(o) => { setIsRulesDialogOpen(o); if (!o) { setEditingRule(null); setRulesPlan(null); } }}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="compensation-rules-dialog">
          <DialogHeader>
            <DialogTitle>
              Compensation Rules {rulesPlan ? `— ${rulesPlan.role} · L${rulesPlan.level}` : ''}
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500">
            Storage only. Rules define WHAT is compensated on WHICH event. No calculations run in this task.
          </p>

          {/* Existing rules list */}
          <div className="mt-3">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-slate-700">Existing rules ({ruleList.length})</p>
              <Button size="sm" variant="outline" onClick={openAddRule} data-testid="add-compensation-rule-btn">
                <Plus className="h-3.5 w-3.5 mr-1" /> Add Rule
              </Button>
            </div>
            {ruleList.length === 0 ? (
              <p className="text-xs text-slate-400" data-testid="compensation-rules-empty">
                No rules yet. Click &quot;Add Rule&quot; to define one.
              </p>
            ) : (
              <div className="space-y-1" data-testid="compensation-rules-list">
                {ruleList.map(r => (
                  <div
                    key={r.id}
                    className="border border-slate-200 rounded p-2 text-sm flex items-start justify-between gap-2 bg-slate-50/50"
                    data-testid={`compensation-rule-row-${r.id}`}
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-slate-800">{r.event}</span>
                        <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100">
                          {r.calculation_type}
                        </span>
                        {r.calculation_type === 'formula' && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500 border border-slate-200" title="Legacy formula rule — stored only, never executed. Edit and pick Fixed or Percentage to make it active.">
                            informational
                          </span>
                        )}
                        {r.status === 'active' ? (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Active</span>
                        ) : (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-200 text-slate-600">Inactive</span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        Value: <span className="font-mono">{r.value == null || r.value === '' ? '—' : r.value}</span>
                        {r.effective_date ? ` · from ${r.effective_date}` : ''}
                        {r.expiration_date ? ` · until ${r.expiration_date}` : ''}
                      </p>
                      {r.notes && <p className="text-xs text-slate-500 italic mt-1">{r.notes}</p>}
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <Button variant="ghost" size="icon" onClick={() => openEditRule(r)} data-testid={`edit-compensation-rule-${r.id}`}>
                        <Edit className="h-3.5 w-3.5 text-slate-500" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDeleteRule(r.id)} data-testid={`delete-compensation-rule-${r.id}`}>
                        <Trash2 className="h-3.5 w-3.5 text-red-500" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Add / Edit form */}
          <form onSubmit={handleSaveRule} className="mt-4 border-t border-slate-200 pt-3 space-y-3 text-sm">
            <p className="text-sm font-medium text-slate-700">
              {editingRule ? 'Edit rule' : 'Add a new rule'}
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="text-[11px] uppercase tracking-wide text-slate-500">Compensation Event *</label>
                <select
                  className="w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm"
                  value={ruleForm.event}
                  onChange={e => setRuleForm(prev => ({ ...prev, event: e.target.value }))}
                  data-testid="compensation-rule-event"
                  required
                >
                  <option value="">— Select event —</option>
                  {COMPENSATION_EVENTS.map(g => (
                    <optgroup key={g.group} label={g.group}>
                      {g.options.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wide text-slate-500">Calculation Type *</label>
                <select
                  className="w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm"
                  value={ruleForm.calculation_type}
                  onChange={e => setRuleForm(prev => ({ ...prev, calculation_type: e.target.value }))}
                  data-testid="compensation-rule-calc-type"
                  required
                >
                  <option value="fixed">Fixed Dollar Amount</option>
                  <option value="percentage">Percentage</option>
                  <option value="formula">Formula (placeholder)</option>
                </select>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wide text-slate-500">
                  Value {ruleForm.calculation_type === 'formula' ? '(placeholder — not executed)' : ''}
                </label>
                <Input
                  value={ruleForm.value}
                  onChange={e => setRuleForm(prev => ({ ...prev, value: e.target.value }))}
                  placeholder={ruleForm.calculation_type === 'fixed' ? 'e.g. 250' : ruleForm.calculation_type === 'percentage' ? 'e.g. 15' : 'e.g. vendor_pmt * 0.10'}
                  data-testid="compensation-rule-value"
                />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wide text-slate-500">Effective Date</label>
                <Input type="date" value={ruleForm.effective_date} onChange={e => setRuleForm(prev => ({ ...prev, effective_date: e.target.value }))} data-testid="compensation-rule-effective-date" />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wide text-slate-500">Expiration Date</label>
                <Input type="date" value={ruleForm.expiration_date} onChange={e => setRuleForm(prev => ({ ...prev, expiration_date: e.target.value }))} data-testid="compensation-rule-expiration-date" />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wide text-slate-500">Status</label>
                <select
                  className="w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm"
                  value={ruleForm.status}
                  onChange={e => setRuleForm(prev => ({ ...prev, status: e.target.value }))}
                  data-testid="compensation-rule-status"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <div className="col-span-2">
                <label className="text-[11px] uppercase tracking-wide text-slate-500">Notes</label>
                <Input value={ruleForm.notes} onChange={e => setRuleForm(prev => ({ ...prev, notes: e.target.value }))} placeholder="Optional context" data-testid="compensation-rule-notes" />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              {editingRule && (
                <Button type="button" variant="outline" onClick={() => { setEditingRule(null); setRuleForm({ event: '', calculation_type: 'fixed', value: '', effective_date: '', expiration_date: '', status: 'active', notes: '' }); }}>
                  Cancel edit
                </Button>
              )}
              <Button type="submit" disabled={savingRule} className="bg-accent hover:bg-gold-dark" data-testid="save-compensation-rule-btn">
                {savingRule ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {editingRule ? 'Update Rule' : 'Add Rule'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>



      {/* v3.15-1188 ADMIN-002 — Role History timeline dialog.
          Renders `user.role_history` (populated server-side on role changes).
          Also surfaces company_history if the employee moved between
          companies, since that is the natural companion to a role change. */}
      <Dialog open={!!historyUser} onOpenChange={(o) => { if (!o) setHistoryUser(null); }}>
        <DialogContent className="max-w-lg" data-testid="role-history-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-slate-600" />
              Role History
            </DialogTitle>
          </DialogHeader>
          {historyUser && (
            <div className="space-y-4 mt-1 text-sm">
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500">Employee</p>
                <p className="font-medium">{historyUser.full_name} — <span className="text-slate-500">{historyUser.email}</span></p>
                <p className="text-xs text-slate-500 mt-1">
                  Current role: <span className="font-medium">{historyUser.role || '—'}</span>
                </p>
              </div>

              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500 mb-2">Role changes</p>
                {(historyUser.role_history || []).length === 0 ? (
                  <p className="text-xs text-slate-400">No role changes recorded yet.</p>
                ) : (
                  <ul className="space-y-2" data-testid="role-history-list">
                    {[...(historyUser.role_history || [])].reverse().map((h, i) => (
                      <li key={i} className="border border-slate-200 rounded p-2">
                        <div className="flex items-center gap-2 text-xs">
                          <span className="text-slate-500 px-1.5 py-0.5 rounded bg-slate-100">
                            {h.previous_role || '—'}
                          </span>
                          <span className="text-slate-400">→</span>
                          <span className="font-medium text-indigo-700 px-1.5 py-0.5 rounded bg-indigo-50">
                            {h.new_role || '—'}
                          </span>
                        </div>
                        <p className="text-[11px] text-slate-500 mt-1">
                          {h.changed_at ? format(new Date(h.changed_at), 'MMM d, yyyy h:mm a') : '—'}
                          {h.changed_by_email ? ` · by ${h.changed_by_email}` : ''}
                        </p>
                        {h.note && (
                          <p className="text-xs text-slate-700 mt-1 italic">“{h.note}”</p>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {(historyUser.company_history || []).length > 0 && (
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-500 mb-2">Company reassignments</p>
                  <ul className="space-y-2" data-testid="company-history-list">
                    {[...(historyUser.company_history || [])].reverse().map((h, i) => {
                      const prevCo = allCompanies.find(c => c.id === h.previous_company_id)?.name || h.previous_company_id || '—';
                      const newCo  = allCompanies.find(c => c.id === h.new_company_id)?.name || h.new_company_id || '—';
                      return (
                        <li key={i} className="border border-slate-200 rounded p-2">
                          <div className="flex items-center gap-2 text-xs">
                            <span className="text-slate-500 px-1.5 py-0.5 rounded bg-slate-100">
                              {prevCo}
                            </span>
                            <span className="text-slate-400">→</span>
                            <span className="font-medium text-emerald-700 px-1.5 py-0.5 rounded bg-emerald-50">
                              {newCo}
                            </span>
                          </div>
                          <p className="text-[11px] text-slate-500 mt-1">
                            {h.changed_at ? format(new Date(h.changed_at), 'MMM d, yyyy h:mm a') : '—'}
                            {h.changed_by_email ? ` · by ${h.changed_by_email}` : ''}
                          </p>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* v3.15-951-INVOICE-WIRE-VIEW-ENABLE: Wire Payment detail modal.
          Click a wire row or the Eye icon to open. Shows FULL notes (no
          truncation), recorded-by, internal id. Edit/Delete inline. */}
      <Dialog open={!!viewPayment} onOpenChange={(o) => { if (!o) setViewPayment(null); }}>
        <DialogContent className="max-w-lg" data-testid="wire-payment-detail-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-4 w-4 text-blue-600" />
              Wire Payment Detail
            </DialogTitle>
          </DialogHeader>
          {viewPayment && (
            <div className="space-y-3 mt-2 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-slate-500">Date</p>
                  <p className="font-medium">{viewPayment.wire_date || '—'}</p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-slate-500">Amount</p>
                  <p className="font-semibold text-green-600" data-testid="wire-detail-amount">{formatCurrency(viewPayment.amount)}</p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-slate-500">Wire Reference</p>
                  <p className="font-medium break-all" data-testid="wire-detail-reference">{viewPayment.wire_reference || '—'}</p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-slate-500">Bank</p>
                  <p className="font-medium">{viewPayment.bank_name || '—'}</p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-slate-500">Client</p>
                  <p className="font-medium">{viewPayment.client_name || '—'}</p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-slate-500">Recorded By</p>
                  <p className="font-medium">{viewPayment.created_by_name || '—'}</p>
                </div>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Notes</p>
                <div
                  className="bg-slate-50 border border-slate-200 rounded p-3 whitespace-pre-wrap max-h-48 overflow-y-auto"
                  data-testid="wire-detail-notes"
                >
                  {viewPayment.notes || <span className="text-slate-400">No notes</span>}
                </div>
              </div>
              <div className="text-[10px] text-slate-400 border-t pt-2">
                Internal ID: <span className="font-mono">{viewPayment.id}</span>
              </div>
              {isInternal() && (
                <div className="flex justify-end gap-2 pt-2 border-t">
                  <Button variant="outline" onClick={openEditPayment} data-testid="wire-detail-edit-btn">
                    <Edit className="h-4 w-4 mr-1" /> Edit
                  </Button>
                  <Button
                    variant="outline"
                    className="text-red-600 border-red-200 hover:bg-red-50"
                    onClick={() => handleDeletePayment(viewPayment.id)}
                    data-testid="wire-detail-delete-btn"
                  >
                    <Trash2 className="h-4 w-4 mr-1" /> Delete
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Wire Payment form (fork of Create for consistency). */}
      <Dialog open={editPaymentOpen} onOpenChange={setEditPaymentOpen}>
        <DialogContent data-testid="wire-payment-edit-modal">
          <DialogHeader><DialogTitle>Edit Wire Payment</DialogTitle></DialogHeader>
          {editPaymentForm && (
            <form onSubmit={savePaymentEdit} className="space-y-4 mt-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Amount *</Label>
                  <Input type="number" step="0.01" value={editPaymentForm.amount}
                    onChange={(e) => setEditPaymentForm({ ...editPaymentForm, amount: e.target.value })} required />
                </div>
                <div>
                  <Label>Date *</Label>
                  <Input type="date" value={editPaymentForm.wire_date}
                    onChange={(e) => setEditPaymentForm({ ...editPaymentForm, wire_date: e.target.value })} required />
                </div>
                <div>
                  <Label>Wire Reference</Label>
                  <Input value={editPaymentForm.wire_reference}
                    onChange={(e) => setEditPaymentForm({ ...editPaymentForm, wire_reference: e.target.value })} />
                </div>
                <div>
                  <Label>Bank</Label>
                  <Input value={editPaymentForm.bank_name}
                    onChange={(e) => setEditPaymentForm({ ...editPaymentForm, bank_name: e.target.value })} />
                </div>
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea rows={4} value={editPaymentForm.notes}
                  onChange={(e) => setEditPaymentForm({ ...editPaymentForm, notes: e.target.value })} />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setEditPaymentOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={savingEdit} data-testid="wire-detail-save-btn">
                  {savingEdit ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
                  Save Changes
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Invoice detail modal */}
      <Dialog open={!!viewInvoice} onOpenChange={(o) => { if (!o) setViewInvoice(null); }}>
        <DialogContent className="max-w-lg" data-testid="invoice-detail-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="h-4 w-4 text-blue-600" />
              Invoice {viewInvoice?.invoice_number}
            </DialogTitle>
          </DialogHeader>
          {viewInvoice && (
            <div className="space-y-3 mt-2 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-slate-500">Invoice #</p>
                  <p className="font-medium" data-testid="invoice-detail-number">{viewInvoice.invoice_number}</p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-slate-500">Client</p>
                  <p className="font-medium">{viewInvoice.client_name || '—'}</p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-slate-500">Amount</p>
                  <p className="font-semibold" data-testid="invoice-detail-amount">{formatCurrency(viewInvoice.amount)}</p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-slate-500">Status</p>
                  <Badge
                    variant={viewInvoice.status === 'paid' ? 'default' : 'secondary'}
                    className={viewInvoice.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}
                    data-testid="invoice-detail-status"
                  >
                    {viewInvoice.status === 'paid'
                      ? (<><CheckCircle className="h-3 w-3 mr-1" /> Paid</>)
                      : (<><Clock className="h-3 w-3 mr-1" /> Pending</>)}
                  </Badge>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-slate-500">Invoice Date</p>
                  <p className="font-medium">{viewInvoice.invoice_date || '—'}</p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-slate-500">Due Date</p>
                  <p className="font-medium">{viewInvoice.due_date || '—'}</p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-slate-500">Paid Date</p>
                  <p className="font-medium">{viewInvoice.paid_date || '—'}</p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-slate-500">Created By</p>
                  <p className="font-medium">{viewInvoice.created_by_name || '—'}</p>
                </div>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Notes</p>
                <div
                  className="bg-slate-50 border border-slate-200 rounded p-3 whitespace-pre-wrap max-h-48 overflow-y-auto"
                  data-testid="invoice-detail-notes"
                >
                  {viewInvoice.notes || <span className="text-slate-400">No notes</span>}
                </div>
              </div>
              <div className="flex flex-wrap justify-end gap-2 pt-2 border-t">
                <Button variant="outline" onClick={printInvoice} data-testid="invoice-detail-print-btn">
                  <Printer className="h-4 w-4 mr-1" /> Print / PDF
                </Button>
                {isInternal() && viewInvoice.status !== 'paid' && (
                  <Button
                    variant="outline"
                    className="text-green-600 border-green-200 hover:bg-green-50"
                    onClick={() => handleMarkInvoicePaid(viewInvoice.id)}
                    data-testid="invoice-detail-mark-paid-btn"
                  >
                    <CheckCircle className="h-4 w-4 mr-1" /> Mark Paid
                  </Button>
                )}
                {isInternal() && (
                  <>
                    <Button variant="outline" onClick={openEditInvoice} data-testid="invoice-detail-edit-btn">
                      <Edit className="h-4 w-4 mr-1" /> Edit
                    </Button>
                    <Button
                      variant="outline"
                      className="text-red-600 border-red-200 hover:bg-red-50"
                      onClick={() => handleDeleteInvoice(viewInvoice.id)}
                      data-testid="invoice-detail-delete-btn"
                    >
                      <Trash2 className="h-4 w-4 mr-1" /> Delete
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Invoice modal */}
      <Dialog open={editInvoiceOpen} onOpenChange={setEditInvoiceOpen}>
        <DialogContent data-testid="invoice-edit-modal">
          <DialogHeader><DialogTitle>Edit Invoice</DialogTitle></DialogHeader>
          {editInvoiceForm && (
            <form onSubmit={saveInvoiceEdit} className="space-y-4 mt-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Invoice Number *</Label>
                  <Input value={editInvoiceForm.invoice_number}
                    onChange={(e) => setEditInvoiceForm({ ...editInvoiceForm, invoice_number: e.target.value })} required />
                </div>
                <div>
                  <Label>Amount *</Label>
                  <Input type="number" step="0.01" value={editInvoiceForm.amount}
                    onChange={(e) => setEditInvoiceForm({ ...editInvoiceForm, amount: e.target.value })} required />
                </div>
                <div>
                  <Label>Invoice Date *</Label>
                  <Input type="date" value={editInvoiceForm.invoice_date}
                    onChange={(e) => setEditInvoiceForm({ ...editInvoiceForm, invoice_date: e.target.value })} required />
                </div>
                <div>
                  <Label>Due Date</Label>
                  <Input type="date" value={editInvoiceForm.due_date}
                    onChange={(e) => setEditInvoiceForm({ ...editInvoiceForm, due_date: e.target.value })} />
                </div>
                <div>
                  <Label>Paid Date</Label>
                  <Input type="date" value={editInvoiceForm.paid_date}
                    onChange={(e) => setEditInvoiceForm({ ...editInvoiceForm, paid_date: e.target.value })} />
                </div>
                <div>
                  <Label>Status</Label>
                  <Select value={editInvoiceForm.status}
                    onValueChange={(v) => setEditInvoiceForm({ ...editInvoiceForm, status: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="paid">Paid</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea rows={4} value={editInvoiceForm.notes}
                  onChange={(e) => setEditInvoiceForm({ ...editInvoiceForm, notes: e.target.value })} />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setEditInvoiceOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={savingEdit} data-testid="invoice-detail-save-btn">
                  {savingEdit ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
                  Save Changes
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CompanyDetailPage;
